/* ==========================================================================
   Momentum — UI
   Header, navigation, search, sliders, facets, popups, countdowns, effects
   ========================================================================== */
(function () {
  'use strict';

  const M = window.Momentum;
  const T = window.theme;
  const { utils } = M;
  const define = (name, klass) => { if (!customElements.get(name)) customElements.define(name, klass); };

  /* --------------------------- Sticky header ---------------------------- */

  class StickyHeader extends M.Element {
    connectedCallback() {
      this.lastScroll = 0;
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.threshold = 120;
      this.onScroll = utils.throttle(() => this.handleScroll(), 80);
      this.on(window, 'scroll', this.onScroll, { passive: true });
      this.measure();
      this.on(window, 'resize', utils.debounce(() => this.measure(), 200));
    }

    measure() {
      const height = this.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height / 10}rem`);
      const announcement = document.querySelector('.announcement-bar');
      if (announcement) {
        document.documentElement.style.setProperty('--announcement-height', `${announcement.offsetHeight}px`);
      }
    }

    handleScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 8);
      if (!this.hideOnScroll) return;
      if (document.body.classList.contains('overflow-hidden')) return;
      const goingDown = y > this.lastScroll && y > this.threshold;
      this.classList.toggle('is-hidden', goingDown);
      this.lastScroll = y;
    }
  }

  /* --------------------------- Generic drawer --------------------------- */

  class SiteDrawer extends M.Element {
    connectedCallback() {
      this.opener = null;
      const id = this.id;
      document.querySelectorAll(`[data-drawer-target="${id}"]`).forEach((btn) =>
        this.on(btn, 'click', (e) => { e.preventDefault(); this.open(btn); })
      );
      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        this.on(btn, 'click', () => this.close())
      );
      this.on(this, 'keyup', (e) => { if (e.key === 'Escape') this.close(); });
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      utils.lockScroll();
      utils.showOverlay(() => this.close());
      setTimeout(() => {
        const focusEl = this.querySelector('[data-drawer-autofocus]') || this.querySelector('[data-drawer-close]');
        utils.trapFocus(this, focusEl);
      }, 60);
      this.dispatchEvent(new CustomEvent('drawer:open'));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      utils.unlockScroll();
      utils.hideOverlay();
      utils.removeTrapFocus(this.opener);
      this.opener = null;
      this.dispatchEvent(new CustomEvent('drawer:close'));
    }
  }

  /* --------------------------- Mobile nav ------------------------------- */

  class MobileNav extends SiteDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.querySelectorAll('[data-panel-open]').forEach((btn) =>
        this.on(btn, 'click', () => {
          const panel = this.querySelector(`[data-panel="${btn.dataset.panelOpen}"]`);
          if (panel) {
            panel.classList.add('is-open');
            const back = panel.querySelector('[data-panel-back]');
            if (back) back.focus();
          }
        })
      );
      this.querySelectorAll('[data-panel-back]').forEach((btn) =>
        this.on(btn, 'click', () => {
          const panel = btn.closest('[data-panel]');
          if (panel) panel.classList.remove('is-open');
        })
      );
      this.addEventListener('drawer:close', () => {
        this.querySelectorAll('[data-panel]').forEach((p) => p.classList.remove('is-open'));
      });
    }
  }

  /* --------------------------- Predictive search ------------------------ */

  class SearchDrawer extends SiteDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.defaultContent = this.querySelector('[data-search-default]');
      this.reset = this.querySelector('[data-search-reset]');
      this.abort = null;

      if (!this.input) return;

      this.on(this.input, 'input', utils.debounce(() => this.search(), 260));
      if (this.reset) {
        this.on(this.reset, 'click', () => {
          this.input.value = '';
          this.input.focus();
          this.showDefault();
        });
      }
      this.addEventListener('drawer:open', () => setTimeout(() => this.input.focus(), 240));
    }

    showDefault() {
      if (this.results) { this.results.innerHTML = ''; this.results.hidden = true; }
      if (this.defaultContent) this.defaultContent.hidden = false;
      if (this.reset) this.reset.hidden = true;
    }

    async search() {
      const term = this.input.value.trim();
      if (this.reset) this.reset.hidden = term.length === 0;
      if (term.length < 2) return this.showDefault();
      if (!T.settings || T.settings.predictiveSearch === false) return;

      if (this.abort) this.abort.abort();
      this.abort = new AbortController();

      const types = [];
      if (this.dataset.products !== 'false') types.push('product');
      if (this.dataset.collections !== 'false') types.push('collection');
      if (this.dataset.articles !== 'false') types.push('article');
      if (this.dataset.pages !== 'false') types.push('page');
      if (this.dataset.queries !== 'false') types.push('query');

      const url = `${T.routes.predictive_search}?q=${encodeURIComponent(term)}&resources[type]=${types.join(',')}&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor,tag&section_id=predictive-search`;

      try {
        const res = await fetch(url, { signal: this.abort.signal });
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const content = doc.querySelector('[data-predictive-results]');
        if (this.results) {
          this.results.innerHTML = content ? content.innerHTML : '';
          this.results.hidden = false;
        }
        if (this.defaultContent) this.defaultContent.hidden = true;
      } catch (e) {
        if (e.name !== 'AbortError') console.error(e);
      }
    }
  }

  /* --------------------------- Slider ----------------------------------- */

  class SliderComponent extends M.Element {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
      this.progress = this.querySelector('[data-slider-progress]');

      if (this.prev) this.on(this.prev, 'click', () => this.scrollBy(-1));
      if (this.next) this.on(this.next, 'click', () => this.scrollBy(1));
      this.dots.forEach((dot, i) => this.on(dot, 'click', () => this.goTo(i)));
      this.on(this.track, 'scroll', utils.throttle(() => this.update(), 100), { passive: true });
      this.on(window, 'resize', utils.debounce(() => this.update(), 200));
      this.update();

      if (this.dataset.autoplay === 'true') this.startAutoplay();
    }

    get step() {
      const first = this.slides[0];
      if (!first) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
      return first.offsetWidth + gap;
    }

    scrollBy(direction) {
      this.track.scrollBy({ left: this.step * direction, behavior: 'smooth' });
    }

    goTo(index) {
      this.track.scrollTo({ left: this.step * index, behavior: 'smooth' });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;

      const index = Math.round(scrollLeft / this.step);
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));

      if (this.progress) {
        const ratio = clientWidth / scrollWidth;
        const offset = scrollWidth > clientWidth ? scrollLeft / (scrollWidth - clientWidth) : 0;
        this.progress.style.width = `${ratio * 100}%`;
        this.progress.style.transform = `translateX(${offset * ((1 / ratio) - 1) * 100}%)`;
      }
    }

    startAutoplay() {
      const delay = Number(this.dataset.autoplaySpeed || 5) * 1000;
      const tick = () => {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        if (scrollLeft + clientWidth >= scrollWidth - 2) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.scrollBy(1);
        }
      };
      this.timer = setInterval(tick, delay);
      this.on(this, 'mouseenter', () => clearInterval(this.timer));
      this.on(this, 'mouseleave', () => (this.timer = setInterval(tick, delay)));
      this.cleanup(() => clearInterval(this.timer));
    }
  }

  /* --------------------------- Slideshow -------------------------------- */

  class Slideshow extends M.Element {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (this.slides.length < 2) return;
      this.dots = Array.from(this.querySelectorAll('[data-slideshow-dot]'));
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = Number(this.dataset.speed || 6) * 1000;

      const prev = this.querySelector('[data-slideshow-prev]');
      const next = this.querySelector('[data-slideshow-next]');
      if (prev) this.on(prev, 'click', () => this.go(this.index - 1));
      if (next) this.on(next, 'click', () => this.go(this.index + 1));
      this.dots.forEach((dot, i) => this.on(dot, 'click', () => this.go(i)));

      this.on(this, 'mouseenter', () => this.stop());
      this.on(this, 'mouseleave', () => this.start());
      this.on(this, 'focusin', () => this.stop());

      // Swipe
      let startX = 0;
      this.on(this, 'touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
      this.on(this, 'touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      this.go(0);
      this.start();
    }

    go(index) {
      const total = this.slides.length;
      this.index = ((index % total) + total) % total;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === this.index);
        slide.setAttribute('aria-hidden', String(i !== this.index));
      });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
    }

    start() {
      if (!this.autoplay || utils.prefersReducedMotion()) return;
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }
    stop() { clearInterval(this.timer); }
    disconnectedCallback() { super.disconnectedCallback(); this.stop(); }
  }

  /* --------------------------- Announcement bar ------------------------- */

  class AnnouncementBar extends M.Element {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement]'));
      this.index = 0;
      if (this.slides.length < 2) return;
      const prev = this.querySelector('[data-announcement-prev]');
      const next = this.querySelector('[data-announcement-next]');
      if (prev) this.on(prev, 'click', () => this.go(this.index - 1));
      if (next) this.on(next, 'click', () => this.go(this.index + 1));
      if (this.dataset.autorotate === 'true' && !utils.prefersReducedMotion()) {
        const speed = Number(this.dataset.speed || 5) * 1000;
        this.timer = setInterval(() => this.go(this.index + 1), speed);
        this.cleanup(() => clearInterval(this.timer));
      }
    }
    go(index) {
      const total = this.slides.length;
      this.index = ((index % total) + total) % total;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
    }
  }

  /* --------------------------- Countdown -------------------------------- */

  class CountdownTimer extends M.Element {
    connectedCallback() {
      this.end = this.parseDate(this.dataset.end);
      if (!this.end) return;
      this.units = {
        days: this.querySelector('[data-unit="days"]'),
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]')
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
      this.cleanup(() => clearInterval(this.timer));
    }

    parseDate(value) {
      if (!value) return null;
      const parsed = new Date(value.replace(' ', 'T'));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    tick() {
      let diff = this.end.getTime() - Date.now();
      if (diff <= 0) {
        clearInterval(this.timer);
        if (this.dataset.onComplete === 'hide') {
          const hideTarget = this.closest('[data-countdown-wrapper]') || this;
          hideTarget.hidden = true;
        } else {
          Object.values(this.units).forEach((el) => el && (el.textContent = '00'));
        }
        return;
      }
      const days = Math.floor(diff / 86400000); diff -= days * 86400000;
      const hours = Math.floor(diff / 3600000); diff -= hours * 3600000;
      const minutes = Math.floor(diff / 60000); diff -= minutes * 60000;
      const seconds = Math.floor(diff / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      if (this.units.days) this.units.days.textContent = pad(days);
      if (this.units.hours) this.units.hours.textContent = pad(hours);
      if (this.units.minutes) this.units.minutes.textContent = pad(minutes);
      if (this.units.seconds) this.units.seconds.textContent = pad(seconds);
    }
  }

  /* --------------------------- Marquee ---------------------------------- */

  class MarqueeText extends M.Element {
    connectedCallback() {
      const track = this.querySelector('[data-marquee-track]');
      const group = this.querySelector('[data-marquee-group]');
      if (!track || !group) return;
      // Duplicate until the track is at least twice the viewport for a seamless loop
      const target = this.offsetWidth * 2;
      let guard = 0;
      while (track.scrollWidth < target && guard < 12) {
        track.appendChild(group.cloneNode(true));
        guard += 1;
      }
    }
  }

  /* --------------------------- Compare slider --------------------------- */

  class CompareSlider extends M.Element {
    connectedCallback() {
      this.range = this.querySelector('input[type="range"]');
      if (!this.range) return;
      const apply = () => this.style.setProperty('--position', `${this.range.value}%`);
      this.on(this.range, 'input', apply);
      apply();
    }
  }

  /* --------------------------- Stats counter ---------------------------- */

  class StatsCounter extends M.Element {
    connectedCallback() {
      this.numbers = Array.from(this.querySelectorAll('[data-count-to]'));
      if (!this.numbers.length || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          this.animate(entry.target);
        });
      }, { threshold: 0.4 });
      this.numbers.forEach((n) => io.observe(n));
      this.cleanup(() => io.disconnect());
    }

    animate(el) {
      if (utils.prefersReducedMotion()) {
        el.textContent = el.dataset.countTo;
        return;
      }
      const target = parseFloat(String(el.dataset.countTo).replace(/[^0-9.-]/g, '')) || 0;
      const prefix = el.dataset.prefix || '';
      const suffix = el.dataset.suffix || '';
      const decimals = (String(el.dataset.countTo).split('.')[1] || '').length;
      const duration = 1400;
      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const value = (target * eased).toFixed(decimals);
        el.textContent = `${prefix}${Number(value).toLocaleString()}${suffix}`;
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }

  /* --------------------------- Scroll reveal ---------------------------- */

  function initReveal() {
    if (!document.body.classList.contains('animate--enabled')) return;
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    const observe = () => document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => io.observe(el));
    observe();
    document.addEventListener('shopify:section:load', observe);
    document.addEventListener('momentum:section-rendered', observe);
  }

  /* --------------------------- Facets / collection ---------------------- */

  class FacetFilters extends M.Element {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.on(this, 'change', (e) => {
        if (e.target.closest('[data-facet-form]')) this.applyFilters(e.target.form);
      });
      this.on(this, 'click', (e) => {
        const clear = e.target.closest('[data-facet-clear]');
        if (clear) {
          e.preventDefault();
          this.render(clear.href);
        }
      });
      this.on(this, 'submit', (e) => {
        const form = e.target.closest('[data-facet-form]');
        if (form) { e.preventDefault(); this.applyFilters(form); }
      });
      this.on(window, 'popstate', () => this.render(window.location.href, false));
    }

    applyFilters(form) {
      if (!form) return;
      const data = new FormData(form);
      const params = new URLSearchParams();
      data.forEach((value, key) => {
        if (String(value).trim() === '') return;
        params.append(key, value);
      });
      const base = form.dataset.baseUrl || window.location.pathname;
      const url = params.toString() ? `${base}?${params.toString()}` : base;
      this.render(url);
    }

    async render(url, pushState = true) {
      const grid = document.querySelector('[data-product-grid]');
      if (grid) grid.classList.add('is-loading');
      try {
        const doc = await utils.getSectionHTML(url, this.sectionId);
        ['[data-product-grid-wrapper]', '[data-facets-sidebar]', '[data-facets-drawer-content]',
         '[data-active-facets]', '[data-collection-count]', '[data-pagination]'].forEach((selector) => {
          const source = doc.querySelector(selector);
          const target = document.querySelector(selector);
          if (source && target) target.innerHTML = source.innerHTML;
          else if (!source && target) target.innerHTML = '';
        });
        if (pushState) window.history.pushState({}, '', url);
        document.dispatchEvent(new CustomEvent('momentum:section-rendered'));
        const anchor = document.querySelector('[data-collection-anchor]');
        if (anchor && pushState) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {
        window.location.href = url;
      } finally {
        if (grid) grid.classList.remove('is-loading');
      }
    }
  }

  class LoadMore extends M.Element {
    connectedCallback() {
      this.button = this.querySelector('[data-load-more-button]');
      this.sectionId = this.dataset.section;
      this.infinite = this.dataset.infinite === 'true';
      if (this.button) this.on(this.button, 'click', () => this.load());
      if (this.infinite && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) this.load();
        }, { rootMargin: '600px' });
        this.io.observe(this);
        this.cleanup(() => this.io.disconnect());
      }
    }

    async load() {
      if (this.loading) return;
      const url = this.dataset.nextUrl;
      if (!url) return;
      this.loading = true;
      if (this.button) this.button.setAttribute('aria-busy', 'true');
      try {
        const doc = await utils.getSectionHTML(url, this.sectionId);
        const newItems = doc.querySelectorAll('[data-product-grid] > li');
        const grid = document.querySelector('[data-product-grid]');
        if (grid) newItems.forEach((item) => grid.appendChild(item));
        const nextLoader = doc.querySelector('[data-load-more]');
        if (nextLoader && nextLoader.dataset.nextUrl) {
          this.dataset.nextUrl = nextLoader.dataset.nextUrl;
          const meta = this.querySelector('[data-load-more-meta]');
          const newMeta = nextLoader.querySelector('[data-load-more-meta]');
          if (meta && newMeta) meta.innerHTML = newMeta.innerHTML;
        } else {
          this.remove();
        }
        document.dispatchEvent(new CustomEvent('momentum:section-rendered'));
      } catch (e) {
        window.location.href = url;
      } finally {
        this.loading = false;
        if (this.button) this.button.removeAttribute('aria-busy');
      }
    }
  }

  /* --------------------------- Localization ----------------------------- */

  class LocalizationSelect extends M.Element {
    connectedCallback() {
      this.button = this.querySelector('[data-localization-button]');
      this.list = this.querySelector('[data-localization-list]');
      this.input = this.querySelector('input[name="country_code"], input[name="locale_code"]');
      this.form = this.closest('form');
      if (!this.button || !this.list) return;

      this.on(this.button, 'click', () => {
        const open = !this.list.hidden;
        this.list.hidden = open;
        this.button.setAttribute('aria-expanded', String(!open));
      });

      this.list.querySelectorAll('button[data-value]').forEach((option) =>
        this.on(option, 'click', () => {
          if (this.input) this.input.value = option.dataset.value;
          if (this.form) this.form.submit();
        })
      );

      this.on(document, 'click', (e) => {
        if (!this.contains(e.target)) {
          this.list.hidden = true;
          this.button.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  /* --------------------------- Share ----------------------------------- */

  class ShareButton extends M.Element {
    connectedCallback() {
      this.button = this.querySelector('[data-share-trigger]');
      this.list = this.querySelector('[data-share-list]');
      this.url = this.dataset.url || window.location.href;
      if (!this.button) return;
      this.on(this.button, 'click', async () => {
        if (navigator.share) {
          try {
            await navigator.share({ title: document.title, url: this.url });
            return;
          } catch (e) { /* user cancelled — fall through */ }
        }
        if (this.list) this.list.hidden = !this.list.hidden;
      });
      this.querySelectorAll('[data-share-copy]').forEach((btn) =>
        this.on(btn, 'click', async () => {
          try {
            await navigator.clipboard.writeText(this.url);
            utils.toast(T.strings.linkCopied);
          } catch (e) { utils.toast(this.url); }
        })
      );
    }
  }

  /* --------------------------- Hotspots -------------------------------- */

  class LookbookHotspot extends M.Element {
    connectedCallback() {
      const dot = this.querySelector('[data-hotspot-dot]');
      if (!dot) return;
      this.on(dot, 'click', (e) => {
        e.preventDefault();
        const wasOpen = this.classList.contains('is-open');
        document.querySelectorAll('lookbook-hotspot.is-open').forEach((h) => h.classList.remove('is-open'));
        this.classList.toggle('is-open', !wasOpen);
      });
      this.on(document, 'click', (e) => {
        if (!this.contains(e.target)) this.classList.remove('is-open');
      });
    }
  }

  /* --------------------------- Deferred video --------------------------- */

  class DeferredMedia extends M.Element {
    connectedCallback() {
      const trigger = this.querySelector('[data-media-play]');
      if (!trigger) return;
      this.on(trigger, 'click', () => this.load());
      if (this.dataset.autoload === 'true') this.load();
    }

    load() {
      if (this.dataset.loaded === 'true') return;
      const template = this.querySelector('template');
      if (!template) return;
      const content = template.content.firstElementChild.cloneNode(true);
      const poster = this.querySelector('[data-media-poster]');
      if (poster) poster.remove();
      this.appendChild(content);
      this.dataset.loaded = 'true';
      const media = this.querySelector('video, iframe, model-viewer');
      if (media && typeof media.play === 'function') media.play().catch(() => {});
      if (media) media.focus && media.focus();
    }
  }

  /* --------------------------- Popup ----------------------------------- */

  class PromoPopup extends M.Element {
    connectedCallback() {
      this.storageKey = `momentum:popup:${this.dataset.popupId || 'default'}`;
      this.frequency = Number(this.dataset.frequencyDays || 7);
      this.querySelectorAll('[data-popup-close]').forEach((btn) =>
        this.on(btn, 'click', () => this.close(true))
      );
      this.on(this, 'keyup', (e) => { if (e.key === 'Escape') this.close(true); });

      const form = this.querySelector('form');
      if (form) this.on(form, 'submit', () => utils.store.set(this.storageKey, Date.now()));

      if (utils.isEditor()) { this.open(); return; }
      if (this.dismissedRecently()) return;

      const delay = Number(this.dataset.delay || 5) * 1000;
      this.timer = setTimeout(() => this.open(), delay);
      this.cleanup(() => clearTimeout(this.timer));

      if (this.dataset.exitIntent === 'true') {
        this.exitHandler = (e) => { if (e.clientY <= 0) this.open(); };
        this.on(document, 'mouseleave', this.exitHandler);
      }
      if (this.dataset.scrollTrigger) {
        const pct = Number(this.dataset.scrollTrigger);
        this.scrollHandler = utils.throttle(() => {
          const scrolled = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
          if (scrolled >= pct) this.open();
        }, 250);
        this.on(window, 'scroll', this.scrollHandler, { passive: true });
      }
    }

    dismissedRecently() {
      const last = utils.store.get(this.storageKey);
      if (!last) return false;
      return Date.now() - last < this.frequency * 86400000;
    }

    open() {
      if (this.classList.contains('is-open') || this.dismissedRecently()) return;
      clearTimeout(this.timer);
      this.classList.add('is-open');
      if (this.dataset.layout === 'center') utils.lockScroll();
      const focusEl = this.querySelector('input, [data-popup-close]');
      if (focusEl) setTimeout(() => focusEl.focus({ preventScroll: true }), 300);
    }

    close(remember) {
      this.classList.remove('is-open');
      if (this.dataset.layout === 'center') utils.unlockScroll();
      if (remember) utils.store.set(this.storageKey, Date.now());
    }
  }

  /* --------------------------- Cookie banner ---------------------------- */

  class CookieBanner extends M.Element {
    connectedCallback() {
      this.key = 'momentum:cookies';
      if (utils.store.get(this.key) && !utils.isEditor()) return;
      setTimeout(() => this.classList.add('is-open'), 1200);
      this.querySelectorAll('[data-cookie-accept]').forEach((btn) =>
        this.on(btn, 'click', () => this.decide(true))
      );
      this.querySelectorAll('[data-cookie-decline]').forEach((btn) =>
        this.on(btn, 'click', () => this.decide(false))
      );
    }
    decide(accepted) {
      utils.store.set(this.key, accepted ? 'accepted' : 'declined');
      this.classList.remove('is-open');
      if (window.Shopify && window.Shopify.customerPrivacy) {
        try {
          window.Shopify.customerPrivacy.setTrackingConsent(accepted, () => {});
        } catch (e) {}
      }
    }
  }

  /* --------------------------- Age gate --------------------------------- */

  class AgeGate extends M.Element {
    connectedCallback() {
      this.key = 'momentum:age-verified';
      if (utils.store.get(this.key) === true && !utils.isEditor()) { this.remove(); return; }
      utils.lockScroll();
      const confirm = this.querySelector('[data-age-confirm]');
      const deny = this.querySelector('[data-age-deny]');
      if (confirm) this.on(confirm, 'click', () => {
        utils.store.set(this.key, true);
        utils.unlockScroll();
        this.remove();
      });
      if (deny) this.on(deny, 'click', () => {
        const denied = this.querySelector('[data-age-denied]');
        const inner = this.querySelector('[data-age-inner]');
        if (denied) denied.hidden = false;
        if (inner) inner.hidden = true;
      });
    }
  }

  /* --------------------------- Generic modal ---------------------------- */

  class SiteModal extends M.Element {
    connectedCallback() {
      this.opener = null;
      document.querySelectorAll(`[data-modal-target="${this.id}"]`).forEach((btn) =>
        this.on(btn, 'click', (e) => { e.preventDefault(); this.open(btn); })
      );
      this.querySelectorAll('[data-modal-close]').forEach((btn) =>
        this.on(btn, 'click', () => this.close())
      );
      this.on(this, 'click', (e) => { if (e.target === this) this.close(); });
      this.on(document, 'keyup', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      utils.lockScroll();
      setTimeout(() => utils.trapFocus(this, this.querySelector('[data-modal-close]')), 60);
    }
    close() {
      this.classList.remove('is-open');
      utils.unlockScroll();
      utils.removeTrapFocus(this.opener);
    }
  }

  /* --------------------------- Accordion group -------------------------- */

  class AccordionGroup extends M.Element {
    connectedCallback() {
      if (this.dataset.exclusive !== 'true') return;
      this.querySelectorAll('details').forEach((details) => {
        this.on(details, 'toggle', () => {
          if (!details.open) return;
          this.querySelectorAll('details[open]').forEach((other) => {
            if (other !== details) other.open = false;
          });
        });
      });
    }
  }

  /* --------------------------- boot ------------------------------------- */

  define('sticky-header', StickyHeader);
  define('site-drawer', SiteDrawer);
  define('mobile-nav', MobileNav);
  define('search-drawer', SearchDrawer);
  define('slider-component', SliderComponent);
  define('slideshow-component', Slideshow);
  define('announcement-bar', AnnouncementBar);
  define('countdown-timer', CountdownTimer);
  define('marquee-text', MarqueeText);
  define('compare-slider', CompareSlider);
  define('stats-counter', StatsCounter);
  define('facet-filters', FacetFilters);
  define('load-more', LoadMore);
  define('localization-select', LocalizationSelect);
  define('share-button', ShareButton);
  define('lookbook-hotspot', LookbookHotspot);
  define('deferred-media', DeferredMedia);
  define('promo-popup', PromoPopup);
  define('cookie-banner', CookieBanner);
  define('age-gate', AgeGate);
  define('site-modal', SiteModal);
  define('accordion-group', AccordionGroup);

  utils.onDocumentReady(initReveal);

  /* Theme editor support */
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('sticky-header').forEach((h) => h.measure && h.measure());
  });
})();
